import { forwardRef, useCallback } from 'react';
import styled from 'styled-components';
import OverlayPlayer from './OverlayPlayer';
import CountPanel from './CountPanel';
import useCountModel from './useCountModel';

/**
 * Two-column overlay results (player + count panel), used by the dev page. The video project page uses the
 * three-column flow in ../../flow/ResultsFlow.jsx, built from the same pieces.
 *
 * Props: tracksDoc (parsed tracks.json), videoSrc (clean_video URL), statistics (video_analyses.statistics),
 * virtualLine (analysis.virtual_line), modelLabel.
 */

const Layout = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 340px;
	gap: 1rem;
	align-items: start;
	@media (max-width: 1100px) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

const OverlayResults = forwardRef(
	(
		{
			tracksDoc,
			videoSrc,
			statistics,
			virtualLine,
			modelLabel = 'BioDrone Foundation Model v2 · reindeer',
		},
		ref
	) => {
		const m = useCountModel({ tracksDoc, statistics, virtualLine });
		const { setFrame } = m;
		const onFrame = useCallback(f => setFrame?.(f), [setFrame]);
		if (!m.index) return null;
		return (
			<Layout>
				<OverlayPlayer
					ref={ref}
					src={videoSrc}
					index={m.index}
					line={m.mode === 'line' ? m.line : null}
					lineEditable={m.mode === 'line'}
					onLineChange={m.setLine}
					onFrame={onFrame}
					aspect={`${m.index.width} / ${m.index.height}`}
				/>
				<CountPanel
					modes={['hover', 'line', 'max']}
					mode={m.mode}
					onModeChange={m.setMode}
					count={m.count}
					subtitle={m.subtitle}
					note={m.note}
					noteWarn={m.noteWarn}
					inView={m.index.inView[m.frame]}
					maxInView={m.index.maxInView}
					hist={m.hist}
					series={m.series}
					frame={m.frame}
					modelLabel={modelLabel}
				/>
			</Layout>
		);
	}
);

OverlayResults.displayName = 'OverlayResults';

export default OverlayResults;
